import React, { FC, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from 'react-query';
import { useActiveClientContext } from 'store/active-client';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buildClientEntity } from 'utils/helpers/client';
import { buildFilters } from 'components/table/utils/filtersConfig';
import { useLanguageContext } from 'store/language';
import MainLayout from 'components/main-layout/MainLayout';
import FetchTable from 'components/table/FetchTable';
import { Column } from 'react-table';
import { formatDate } from 'utils/helpers/formatters';
import sanitizeHTML from 'utils/helpers/editor';
import Button from 'components/Button';
import API from 'core/Api';
import TooltipWrapper from 'components/TooltipWrapper';
import {
  FaFileAlt,
  FaFileUpload,
  FaPrint,
  FaPlus,
  FaPen,
  FaCheck,
  FaArchive,
  FaTimesCircle,
  FaInbox,
  FaChevronLeft,
  FaTrash,
} from 'react-icons/fa';
import { IoSendSharp } from 'react-icons/io5';
import styled from 'styled-components';
import { ISelectOptionProps } from 'components/form/types';
import { IDocumentActivity } from './types';

const Screen: FC = () => {
  const { id } = useParams<{ id: string }>();
  const { client } = useActiveClientContext();
  const history = useHistory();
  const location = useLocation<{ fromReports?: boolean }>();
  const { language } = useLanguageContext();
  const { t } = useTranslation();

  const renderDocNumber = (docNumber: string) => docNumber.padStart(8, '0');
  const renderIcon = (value: string) => {
    if (value === 'create') return <FaPlus size={16} />;
    if (value === 'approve') return <FaCheck size={16} />;
    if (value === 'download') return <FaPrint size={16} />;
    if (value === 'sign') return <FaPen size={16} />;
    if (value === 'send-to-employee' || value === 'send-to-employer') return <IoSendSharp size={16} />;
    if (value === 'receive-by-employee' || value === 'receive-by-employer') return <FaInbox size={16} />;
    if (value === 'archive') return <FaArchive size={16} />;
    if (value === 'reject') return <FaTimesCircle size={16} />;
    if (value === 'moved-to-bin') return <FaTrash size={16} />;
    return null;
  };

  const {
    isLoading: isLoadingDocDropdowns,
    error: errorDocDropdowns,
    data: dropdowns,
  } = useQuery<{ [key: string]: ISelectOptionProps[] }, Error>(
    [buildClientEntity('multiple-document-logs-dropdowns', client)],
    (): Promise<{ [key: string]: ISelectOptionProps[] }> => {
      return API.get(`multiple-document-logs/${id}/dropdown-options`).then(
        res => res.data && res.data[language || 'bg']
      );
    }
  );

  const columns: Array<Column<IDocumentActivity>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('documents.activity.action') || '',
      accessor: 'action',
      width: '5%',
      disableSortBy: true,
      Cell: ({ value }) => {
        return <div className="text-center">{renderIcon(value)}</div>;
      },
    },
    {
      Header: t('documents.activity.number') || '',
      accessor: 'number',
      width: '8%',
      Cell: ({ row }) => {
        return <span>{row.original.document.number}</span>;
      },
    },
    {
      Header: t('documents.activity.description') || '',
      accessor: 'action_description',
      disableSortBy: true,
      Cell: ({ value }) => <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(value) }} />,
    },
    {
      Header: t('documents.activity.date') || '',
      accessor: 'created_at',
      width: '20%',
      disableSortBy: true,
      Cell: ({ value }) => <>{formatDate(new Date(value), 'dd.mm.Y hh:ii:ss')}</>,
    },
    {
      Header: () => <div className="text-right">{t('documents.activity.receipt')}</div>,
      accessor: 'receipt',
      width: '5%',
      disableSortBy: true,
      Cell: ({ row }) => (
        <div className="text-right">
          <TooltipWrapper
            id="tooltip-print"
            overlay={t('documents.table.preview')}
          >
            <Button
              type="button"
              btnType="dark"
              size="sm"
              outlined
              onClick={() => {
                API.get(`document-logs/${row.original.id}/download`).then(res => {
                  window.open(res.data, '_blank');
                });
              }}
            >
              <FaPrint size={14} />
            </Button>
          </TooltipWrapper>
        </div>
      ),
    },
  ];
  const renderDocIcon = (type: string) => {
    if (type === 'internal')
      return (
        <TooltipWrapper
          id="tooltip-internalDoc"
          overlay={t('documents.internalDoc')}
        >
          <FaFileAlt
            size={18}
            color="#32bfa3"
          />
        </TooltipWrapper>
      );
    if (type === 'external')
      return (
        <TooltipWrapper
          id="tooltip-externalDoc"
          overlay={t('documents.externalDoc')}
        >
          <FaFileUpload
            size={18}
            color="#32bfa3"
          />
        </TooltipWrapper>
      );

    return null;
  };

  const buildTableFilters = () => {
    return [
      buildFilters({
        name: 'affected_id',
        placeholder: t('documents.activity.userFilter'),
        options: dropdowns?.user,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'document_id',
        placeholder: t('documents.activity.docFilter'),
        options: dropdowns?.document,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'action',
        placeholder: t('documents.activity.action'),
        options: dropdowns?.action,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
    ];
  };

  return (
    <MainLayout title={`${t('documents.docActivity')} ${formatDate(new Date(), 'dd.mm.Y hh:ii:ss')}`}>
      <FetchTable
        entityName={buildClientEntity('multiple-document-logs', client)}
        entityEndpoint={`multiple-document-logs/${id}`}
        loading={isLoadingDocDropdowns}
        columns={columns}
        pagination
        pageLimit={10}
        filters={buildTableFilters()}
        hasTitle
        updateTitle={data => {
          if (!document && data?.items.length) {
            document = data?.items[0].document;
          }
          return (
            <TitleWrapper>
              <div>
                {renderDocIcon(document.type)}
                <span className="ml-2">
                  {document?.name} {t('common.with')} &#8470;&nbsp;
                  {renderDocNumber(id)}
                </span>
              </div>
              <Button
                type="button"
                btnType="secondary"
                outlined
                className="ml-4"
                onClick={() => history.goBack()}
              >
                <FaChevronLeft
                  size={12}
                  style={{ marginRight: '0.5rem' }}
                />
                {location?.state?.fromReports
                  ? t('documents.activity.backToReports')
                  : t('documents.activity.backToDocsList')}
              </Button>
            </TitleWrapper>
          );
        }}
      />
    </MainLayout>
  );
};

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
let document: any;

export default Screen;
